import { usePreviousValue } from '@moda/portal-stanchions';
import { pick, symmetricDifference } from 'ramda';
import { useEffect, useMemo } from 'react';
import { SortOption } from '../constants';
import { AvailabilityInput } from '../generated/types/globalTypes';
import { ProductListPageResultFragment_variants_edges_node } from '../generated/types/ProductListPageResultFragment';
import { tc } from '../lib/trackingContext';
import { usePLPParams } from './usePLPParams';

type Type = {
  variants: ProductListPageResultFragment_variants_edges_node[];
  availableSorts: SortOption[];
  loading: boolean;
};

const FILTER_FIELDS = ['availability', 'attributes', 'designers', 'colors', 'sizes'];

export const useFilterEngagementTracking = ({ variants, loading, availableSorts }: Type) => {
  const { params, currentSort } = usePLPParams(availableSorts);
  const previousParams = usePreviousValue(params);
  const previousSort = usePreviousValue(currentSort);

  const selectedParams = useMemo(
    () => pick([...FILTER_FIELDS, 'pricemin', 'pricemax'], params),
    [params]
  );

  const changedFilter =
    previousParams &&
    symmetricDifference(Object.entries(params), Object.entries(previousParams))[0];

  const filteringTriggered = Boolean(changedFilter && FILTER_FIELDS.includes(changedFilter[0]));
  const sortingTriggered = Boolean(previousSort && previousSort?.value !== currentSort.value);

  const sanitizedFilters = useMemo(() => {
    const filters: { type: string; value: string | number | string[] | AvailabilityInput[] }[] = [];

    Object.entries(selectedParams).map(([key, value]) => {
      if ((!Array.isArray(value) && value) || (Array.isArray(value) && value.length > 0)) {
        filters.push({ type: key, value: value });
      }
    });

    return filters;
  }, [selectedParams]);

  useEffect(() => {
    if ((filteringTriggered || sortingTriggered) && !loading) {
      tc.track('Product List Filtered', {
        addToScopeOnce: {
          products: variants,
          filters: sanitizedFilters,
          sorts: [{ type: currentSort.name, value: currentSort.value }],
          label: JSON.stringify(sanitizedFilters)
        }
      });
    }
  }, [variants, loading, currentSort, filteringTriggered, sortingTriggered, sanitizedFilters]);
};
